/**
 * Data hook that writes a chat's full per-distributor-user notification
 * preference list (`deselectedUsersList`) via the V2 mutation.
 *
 * Used by the create flow to apply the operator's initial notification choices
 * after `chatCreate`. It is silent by design (no toasts) so the create form
 * stays the single owner of user-facing notifications; failures are surfaced
 * through `onError` and by throwing. A brand-new chat has no existing list to
 * merge with, so the create flow sets the sanitized list directly rather than
 * doing the read-then-merge the per-toggle Edit hook performs.
 */

import { useCallback } from 'react';

import { useChatsPageUpdateChatNotificationPreferencesV2Mutation } from '../../../generated';

export interface UseSetChatNotificationPreferencesConfig {
  onError?: (error: Error) => void;
}

export interface SetChatNotificationPreferencesInput {
  chatId: string;
  /** Distributor user ids whose new-order notifications are turned OFF. */
  deselectedUsersList: string[];
}

export const useSetChatNotificationPreferences = (
  config: UseSetChatNotificationPreferencesConfig = {}
) => {
  const { onError } = config;

  const [mutate, { loading }] =
    useChatsPageUpdateChatNotificationPreferencesV2Mutation({
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
    });

  const setNotificationPreferences = useCallback(
    async ({
      chatId,
      deselectedUsersList,
    }: SetChatNotificationPreferencesInput) => {
      try {
        const { data, errors } = await mutate({
          variables: {
            input: {
              id: chatId,
              newOrder: { deselectedUsersList },
            },
          },
        });

        if (errors && errors.length > 0) {
          throw new Error(
            errors[0].message || 'Failed to update notification preferences.'
          );
        }

        if (!data?.updateChatNotificationPreferencesV2) {
          throw new Error(
            'Notification preferences update returned no result.'
          );
        }
      } catch (caughtError) {
        const error =
          caughtError instanceof Error
            ? caughtError
            : new Error('Failed to update notification preferences.');
        onError?.(error);
        throw error;
      }
    },
    [mutate, onError]
  );

  return {
    setNotificationPreferences,
    isUpdating: loading,
  };
};
